import React, { useEffect, useMemo, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { createThemeByMode } from "@rusha/shared-theme";
import { BrowserRouter as Router, useRoutes } from "react-router";

import { AuthProvider } from "src/domains/auth/module/components/AuthProvider";
import { eventBus, registerDomainModules } from "src/platform/events/EventBus";
import { useThemeManager } from "src/platform/hooks/useThemeManager";
import {
  StateMachineProvider,
  useStateMachineContext,
} from "src/platform/state/StateMachineProvider";
import {
  ErrorBoundary,
  ErrorFallback,
} from "src/shell/components/ErrorBoundary";
import { NotificationProvider } from "src/shell/contexts/sharedNotificationContext";

import InitializationLoading from "./domains/loading/pages/InitializationLoading";
import { ComponentRegistry } from "./platform/config/ComponentRegistry";
import { DomainRegistryProvider } from "./platform/config/DomainRegistryProvider";
import { PlatformConfigManager } from "./platform/config/PlatformConfig";
import { PlatformConfigProvider } from "./platform/config/PlatformConfigProvider";
import { RouteRegistry } from "./platform/config/RouteRegistry";
import { FeatureFlags } from "./platform/feature-flags/FeatureFlags";
import { ServiceRegistry } from "./platform/services/ServiceRegistry";
import { GateManager } from "./platform/state/GateManager";
import { AppState } from "./platform/state/types";
import debugLogger from "./platform/utils/debugLogger";
import { routes } from "./routes";

// Register all domain modules with EventBus at startup
registerDomainModules();

// Theme wrapper component that provides dynamic theme based on mode
function ThemeWrapper({ children }: { children: React.ReactNode }) {
  const { mode } = useThemeManager();

  const currentTheme = useMemo(() => {
    // Use createThemeByMode for proper dark/light palette
    return createThemeByMode(mode);
  }, [mode]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function AppRoutes() {
  return useRoutes(routes);
}

// Component to conditionally render routes based on state machine state
function ConditionalRouter() {
  const stateMachine = useStateMachineContext();
  const isInitializing = stateMachine.currentState === AppState.INITIALIZATION;
  const isError = stateMachine.currentState === AppState.ERROR;

  // Check if error occurred during initialization phase
  const currentPhaseState = stateMachine.currentPhase;
  const isInitializationError =
    isError &&
    (currentPhaseState === AppState.INITIALIZATION ||
      currentPhaseState === null);

  if (isInitializing || isInitializationError) {
    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <InitializationLoading eventBus={eventBus} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <Router>
        <AppRoutes />
      </Router>
    </ErrorBoundary>
  );
}

interface AppProps {
  serviceRegistry: ServiceRegistry;
  gateManager: GateManager;
}

function App({ serviceRegistry, gateManager }: AppProps) {
  const [platformConfig, setPlatformConfig] =
    useState<PlatformConfigManager | null>(null);
  const [componentRegistry, setComponentRegistry] =
    useState<ComponentRegistry | null>(null);
  const [routeRegistry, setRouteRegistry] = useState<RouteRegistry | null>(
    null,
  );
  const [isPlatformConfigReady, setIsPlatformConfigReady] = useState(false);
  const [featureFlags] = useState(() => new FeatureFlags());

  // Initialize platform configuration
  useEffect(() => {
    const initializePlatformConfig = async () => {
      try {
        const config = new PlatformConfigManager(featureFlags);
        await config.initialize("/config/platform.config.yml");

        const configData = config.getConfig();
        if (configData) {
          featureFlags.initializeFromPlatformConfig(configData);
        }

        const compRegistry = new ComponentRegistry(config, featureFlags);
        const rtRegistry = new RouteRegistry(config, featureFlags);

        setPlatformConfig(config);
        setComponentRegistry(compRegistry);
        setRouteRegistry(rtRegistry);
        setIsPlatformConfigReady(true);

        debugLogger.debug("Platform configuration initialized successfully");
      } catch (error) {
        debugLogger.error(
          "Failed to initialize platform configuration:",
          error,
        );
        setPlatformConfig(null);
        setComponentRegistry(null);
        setRouteRegistry(null);
        setIsPlatformConfigReady(true);
      }
    };

    void initializePlatformConfig();
  }, [featureFlags]);

  // Show loading state while platform config is initializing
  if (!isPlatformConfigReady) {
    return (
      <ThemeWrapper>
        <ErrorBoundary fallback={ErrorFallback}>
          <InitializationLoading eventBus={eventBus} />
        </ErrorBoundary>
      </ThemeWrapper>
    );
  }

  return (
    <ThemeWrapper>
      <ErrorBoundary fallback={ErrorFallback}>
        <PlatformConfigProvider
          platformConfig={platformConfig}
          componentRegistry={componentRegistry}
          routeRegistry={routeRegistry}
          featureFlags={featureFlags}
        >
          <DomainRegistryProvider>
            <StateMachineProvider
              eventBus={eventBus}
              serviceRegistry={serviceRegistry}
              gateManager={gateManager}
              autoStart={true}
              onStateChange={(from, to, event) => {
                debugLogger.debug(
                  `State transition: ${from} -> ${to} (event: ${event})`,
                );
              }}
              onError={(error, context) => {
                debugLogger.error(`State machine error in ${context}:`, error);
              }}
            >
              <NotificationProvider>
                <AuthProvider>
                  <ConditionalRouter />
                </AuthProvider>
              </NotificationProvider>
            </StateMachineProvider>
          </DomainRegistryProvider>
        </PlatformConfigProvider>
      </ErrorBoundary>
    </ThemeWrapper>
  );
}

export default App;
